import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Mail, Phone, MapPin, Send, CheckCircle, Loader2, MessageSquare } from 'lucide-react';

const EnquireNow = () => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', exam: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    // Save the lead to backend first so we don't lose it if the user closes WhatsApp
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: form.email || null,
          exam: form.exam || null,
          message: form.message || null,
          source: 'enquire-now',
        }),
      });
    } catch (err) { /* non-blocking — still open WhatsApp */ }

    const text = `Hi VIDYA365! I'd like to enquire about your coaching.%0A%0AName: ${encodeURIComponent(form.name)}%0APhone: ${encodeURIComponent(form.phone)}%0AEmail: ${encodeURIComponent(form.email)}%0AInterested in: ${encodeURIComponent(form.exam)}%0AMessage: ${encodeURIComponent(form.message)}`;
    window.open(`https://wa.me/918504110365?text=${text}`, '_blank');
    setSubmitted(true);
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="bg-gradient-to-br from-red-700 to-red-600 text-white py-12">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h1 className="text-3xl md:text-5xl font-bold mb-3">Enquire Now</h1>
          <p className="text-red-100 max-w-2xl mx-auto">Have questions about course details, fees, batch timings, or enrollment? Fill the form below and we'll get back to you within 30 minutes during working hours.</p>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <Card>
                <CardContent className="p-6">
                  {submitted ? (
                    <div className="text-center py-10">
                      <CheckCircle className="mx-auto mb-3 text-green-600" size={56} />
                      <h2 className="text-2xl font-bold mb-2">Thanks for reaching out!</h2>
                      <p className="text-gray-600 mb-4">We just opened WhatsApp with your enquiry pre-filled. Send it and our counsellor will reply shortly.</p>
                      <a href="tel:+918504110365">
                        <Button data-testid="enquire-call-btn" className="bg-red-600 hover:bg-red-700"><Phone size={16} className="mr-2" /> Call Now Instead</Button>
                      </a>
                    </div>
                  ) : (
                    <form onSubmit={onSubmit} className="space-y-3" data-testid="enquire-form">
                      <h2 className="text-xl font-bold mb-3">Send Us Your Enquiry</h2>
                      <input required name="name" value={form.name} onChange={onChange} placeholder="Your Name *" className="w-full px-3 py-2 border rounded-lg" data-testid="enquire-name" />
                      <input required name="phone" type="tel" pattern="[0-9]{10}" value={form.phone} onChange={onChange} placeholder="10-digit Mobile *" className="w-full px-3 py-2 border rounded-lg" data-testid="enquire-phone" />
                      <input name="email" type="email" value={form.email} onChange={onChange} placeholder="Email (optional)" className="w-full px-3 py-2 border rounded-lg" data-testid="enquire-email" />
                      <select name="exam" value={form.exam} onChange={onChange} className="w-full px-3 py-2 border rounded-lg bg-white" data-testid="enquire-exam">
                        <option value="">Interested In —</option>
                        <option>SSC CGL</option><option>SSC CHSL</option><option>SBI PO</option>
                        <option>IBPS PO</option><option>RBI Grade B</option><option>NABARD Grade A</option>
                        <option>IB ACIO</option><option>SSC MTS</option><option>SSC CPO</option>
                        <option>SBI Clerk</option><option>IBPS Clerk</option><option>Other</option>
                      </select>
                      <textarea name="message" rows={4} value={form.message} onChange={onChange} placeholder="Your question or comments..." className="w-full px-3 py-2 border rounded-lg" data-testid="enquire-message" />
                      <Button type="submit" disabled={submitting} className="w-full bg-red-600 hover:bg-red-700" data-testid="enquire-submit">
                        {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send className="mr-2" size={16} />}
                        Send Enquiry
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
            <div>
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-6 space-y-4">
                  <h3 className="font-bold">Or Contact Us Directly</h3>
                  <a href="tel:+918504110365" className="flex items-center gap-2 text-gray-800 hover:text-red-600"><Phone size={16} /> +91 85041 10365</a>
                  <a href="https://wa.me/918504110365" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-gray-800 hover:text-green-600"><MessageSquare size={16} /> WhatsApp Us</a>
                  <a href="mailto:info@vidya365.com" className="flex items-center gap-2 text-gray-800 hover:text-red-600"><Mail size={16} /> info@vidya365.com</a>
                  <div className="flex items-start gap-2 text-gray-700 text-sm pt-3 border-t">
                    <MapPin size={16} className="flex-shrink-0 mt-1" />
                    <p>Unit 8, Apsara Arcade, Near Karol Bagh Metro Gate 7, Pusa Road, New Delhi - 110005</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EnquireNow;
